import { HttpClient } from '@angular/common/http';
import { IAppResult } from '@app/core/model/appresults/appresult';
import { IAppResultFile } from '@app/core/model/appresults/appresult-file';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BytesFilter } from '@app/core/utilities/filters/bytes.filter';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';

export enum AppResultSearchColumnFields {
    Name = 'Name',
    Path = 'Path',
    Analysis = 'Analysis',
    AppResult = 'AppResult',
    Project = 'Project',
    Size = 'Size',
    Created = 'Created',
    Owner = 'Owner',
}

export class AppResultSearchGridOptionsFormatter extends SearchGridOptionsFormatter<AppResultSearchColumnFields> {
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
        public searchResourceDictionaryService: SearchResourceDictionaryService,
        public bsshDatePipe: BsshDatePipe,
        public http: HttpClient
    ) {
        super(searchResourceDictionaryService, SearchResourceType.AppResult);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
        return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
            params: {
                limit: '50',
                scope: 'projects',
                sortBy: 'ModifiedOn',
                sortDir: 'desc',
                query: arg ? `Project.Name:"${arg}"` : ''
            },
            withCredentials: true
        });
    }

    updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
        return this.getProjectFilterOptions(arg).pipe(
          map((response) => {
            return this.toProjectFilterOptions(response);
          })
        );
    }

    bindFilterUpdateCallbacksToCurrentContext() {
        this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
    }

    toProjectFilterOptions(result): FilterOption[] {
        const projects: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        projects.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.Project.Name,
            filterValue: item.Project.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<AppResultSearchColumnFields> {
        const inclusiveColumns: AppResultSearchColumnFields[] = [
            AppResultSearchColumnFields.Name,
            AppResultSearchColumnFields.AppResult,
            AppResultSearchColumnFields.Size,
            AppResultSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<AppResultSearchColumnFields>[] = [
            {
              colId: AppResultSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const defaultColumnDefinitions = [
            {
                headerName: 'Name',
                field: 'AppResult.Name',
                colId: AppResultSearchColumnFields.Name,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE,
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if (params.data) {
                        const appResult = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IAppResult;
                        if (appResult && appResult.AppSession && !appResult.IsDeleted && !appResult.IsArchived) {
                            const appSession = appResult.AppSession;
                            const fileContainerPath = BsApiEndPoints.getAppResultRoute(appSession.Id, appResult.Id);

                            return DataTableUtilities.getRadLinkParams(
                                appResult.Name,
                                fileContainerPath
                            );
                        } else if (appResult) {
                            return DataTableUtilities.getRadLinkParams(appResult.Name);
                        }
                    }
                    return {};
                },
            },
            {
                headerName: 'Path',
                colId: AppResultSearchColumnFields.Path,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE,
                cellRendererSelector: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        return DataTableUtilities.getSpanRenderer(resource.Path);
                    } else {
                        return DataTableUtilities.getSpanRenderer();
                    }
                },
            },
            {
                headerName: 'Analysis',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IAppResult;
                        if (resource && resource.AppSession) {
                            const appSession = resource.AppSession;
                            return DataTableUtilities.getRadLinkParams(
                                appSession.Name,
                                BsApiEndPoints.getAnalysesRoute(appSession.Id),
                            );
                        }
                    }
                    return {};
                },
                colId: AppResultSearchColumnFields.Analysis,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'App Result',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource: IAppResultFile = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                        if (resource && resource.ParentAppResult && resource.ParentAppResult.AppSession) {
                            const appResult = resource.ParentAppResult;
                            const appSession = appResult.AppSession;
                            return DataTableUtilities.getRadLinkParams(
                                appSession.Name,
                                BsApiEndPoints.getAppResultRoute(appSession.Id, appResult.Id)
                            );
                        }
                    }
                    return {};
                },
                colId: AppResultSearchColumnFields.AppResult,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Project',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const resource = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IAppResult;
                        if (resource && resource.Projects && resource.Projects.length > 0) {
                            const project = resource.Projects[0];
                            return DataTableUtilities.getRadLinkParams(
                                project.Name,
                                BsApiEndPoints.getProjectDetailsRoute(project.Id)
                            );
                        }
                    }
                    return {};
                },
                colId: AppResultSearchColumnFields.Project,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback,
            },
            {
                headerName: 'Size',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    const resource = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                    return BytesFilter(Number(resource.TotalSize));
                  },
                colId: AppResultSearchColumnFields.Size,
                cellRenderer: 'span',
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.SIZE
            },
            {
                headerName: 'Created',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    const resource = this.searchResourceDictionaryService.getSearchResultResource(params.data, this.resourceType);
                    return this.bsshDatePipe.transform(resource.DateCreated);
                    },
                filterActive: false,
                sortable: false,
                colId: AppResultSearchColumnFields.Created,
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE
            },
            {
                headerName: 'Owner',
                field: 'AppResult.UserOwnedBy.Name',
                cellRenderer: 'span',
                colId: AppResultSearchColumnFields.Owner,
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE
            },
        ];
        return defaultColumnDefinitions;
    }
}
